import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiDelete, apiGet, apiGetBlob, apiPost, apiPut, apiUpload } from '../../lib/api';

/**
 * Registrul de documente, dinspre client.
 *
 * Schemele Zod sunt declarate aici, nu importate din `@pcrm/be/documents`: `apps/crm` e
 * `scope:fe` și n-are voie să depindă de backend. Granița se validează în ambele capete.
 */

/**
 * Stările din `crm_documents.document_state`.
 *
 * `5 reziliat` a intrat la [#140](https://github.com/bplugaru/efx-pcrm/issues/140): un act
 * semnat care nu mai produce efecte. **Nu e `anulat` cu alt motiv** — anulatul n-ar fi trebuit
 * să existe, reziliatul a existat și s-a terminat, iar peste un an registrul e singurul loc
 * care mai știe care dintre ele a fost.
 * ⚠️ **`1` s-a numit „generat" până la [#142](https://github.com/bplugaru/efx-pcrm/issues/142).**
 * Numele era chiar confuzia: `documentState = 1` și `source = 1` aveau aceeași etichetă și
 * înțelesuri diferite. Un act generat n-are nicio putere juridică — e o **ciornă**, n-are
 * număr și nu e în registrul de acte emise. Valoarea din bază rămâne `1`.
 *
 * ⚠️ **Pe un act primit, `1` nu înseamnă „ciornă"**, ci „nesemnat, e la dosar"
 * ([#150](https://github.com/bplugaru/efx-pcrm/issues/150)) — el are număr din clipa
 * încărcării. Ce deosebește ciorna de el e **numărul**, nu starea: vezi `isDraft`.
 */
export const DOCUMENT_STATE = {
  draft: 1,
  sent: 2,
  signed: 3,
  cancelled: 4,
  terminated: 5,
} as const;

export type DocumentState = (typeof DOCUMENT_STATE)[keyof typeof DOCUMENT_STATE];

export interface DocumentStateDescriptor {
  readonly value: DocumentState;
  readonly label: string;
  readonly color: 'grey' | 'blue' | 'green' | 'red' | 'orange';
}

/**
 * Eticheta și culoarea fiecărei stări. **Sursa de adevăr a interfeței** — ecranul citește
 * din tabelul ăsta în loc să înșire `if`-uri.
 *
 * `trimis` și `semnat` apar aici deși E4 nu le produce: vin din sesiunea de semnare, iar un
 * registru care nu știe să le deseneze ar arăta rânduri fără stare imediat ce E7 intră.
 */
export const DOCUMENT_STATE_LIST: readonly DocumentStateDescriptor[] = [
  { value: DOCUMENT_STATE.draft, label: 'Ciornă', color: 'grey' },
  { value: DOCUMENT_STATE.sent, label: 'Trimis', color: 'blue' },
  { value: DOCUMENT_STATE.signed, label: 'Semnat', color: 'green' },
  { value: DOCUMENT_STATE.cancelled, label: 'Anulat', color: 'red' },
  // Portocaliu, nu roșu: „reziliat" nu e o greșeală reparată, e un contract dus până la
  // capăt. Roșul l-ar fi citit ochiul ca pe încă o anulare, exact distincția pentru care
  // starea a primit valoare proprie.
  { value: DOCUMENT_STATE.terminated, label: 'Reziliat', color: 'orange' },
];

export function documentStateDescriptor(value: DocumentState): DocumentStateDescriptor {
  return DOCUMENT_STATE_LIST.find((state) => state.value === value) ?? DOCUMENT_STATE_LIST[0];
}

/**
 * E o **ciornă**? — act care n-a fost emis niciodată. Copie a regulii de pe server (`isDraft`).
 *
 * ⚠️ **Se citește din lipsa numărului, nu din stare** (#142): un act **primit** e `1` și are
 * număr. Scrisă pe stare, ar fi oferit „Șterge" pe un PDF scanat.
 */
export function isDraft(document: DocumentStanding): boolean {
  return document.documentNumber === null;
}

/** Cât se citește dintr-un document ca să se știe ce butoane are. Vezi `DocumentStanding` pe server. */
export interface DocumentStanding {
  readonly documentState: DocumentState;
  readonly documentNumber: number | null;
}

/**
 * Se poate anula? **Numai un act emis** — unul cu număr consumat din registrul firmei.
 * Copie a regulii de pe server (`canCancel`).
 *
 * Ce trece pe aici după #142 sunt actele **primite** nesemnate. Un act generat în `ciornă`
 * sau `trimis` n-are număr: el se **șterge**.
 */
export function canCancel(document: DocumentStanding): boolean {
  return (
    !isDraft(document) &&
    (document.documentState === DOCUMENT_STATE.draft ||
      document.documentState === DOCUMENT_STATE.sent)
  );
}

/**
 * Se poate **șterge**? Doar o ciornă. Copie a regulii de pe server (`canDelete`).
 *
 * Ștergerea e singura operație din registru care nu se poate desface, deci starea rămâne în
 * condiție deși numărul ar fi fost de ajuns.
 */
export function canDelete(document: DocumentStanding): boolean {
  return (
    isDraft(document) &&
    (document.documentState === DOCUMENT_STATE.draft ||
      document.documentState === DOCUMENT_STATE.sent)
  );
}

/**
 * Se poate rezilia **doar un act semnat**. Copie a regulii de pe server (`canTerminate`).
 *
 * Cele două porți sunt disjuncte: ce se anulează nu se reziliază și invers. Meniul arată deci
 * exact una dintre acțiuni, niciodată amândouă și niciodată una stinsă — un rând gri într-un
 * meniu cere o explicație pe care n-avem unde s-o dăm.
 * Vezi [#140](https://github.com/bplugaru/efx-pcrm/issues/140).
 */
export function canTerminate(state: DocumentState): boolean {
  return state === DOCUMENT_STATE.signed;
}

/**
 * Se poate arhiva un act **ieșit din circulație**: anulat sau reziliat. Copie a regulii de pe
 * server (`canArchive`).
 *
 * Un act **semnat** nu se arhivează, pentru că e în vigoare — ce e activ n-are ce căuta ascuns.
 * O **ciornă** greșită se șterge (#142); un act primit nesemnat se anulează întâi.
 * Vezi [#141](https://github.com/bplugaru/efx-pcrm/issues/141) și
 * [#140](https://github.com/bplugaru/efx-pcrm/issues/140).
 */
export function canArchive(state: DocumentState): boolean {
  return state === DOCUMENT_STATE.cancelled || state === DOCUMENT_STATE.terminated;
}

/**
 * Stările, ca schemă. **Exportată** pentru că și alte ecrane citesc starea unui act de la
 * server — istoricul abonamentului o arată lângă numărul actului (#163) — iar o a doua
 * uniune scrisă acolo ar fi rămas în urmă la prima stare nouă, fără ca nimic să pice.
 */
export const documentStateSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
]);

const documentSchema = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  documentTypeId: z.uuid(),
  customerId: z.uuid().nullable(),
  /** Partenerul, când actul e al lui. Exact unul dintre cele două e completat (#133). */
  partnerId: z.uuid().nullable(),
  /**
   * Numărul din registrul firmei, sau **`null` cât actul e ciornă**
   * ([#142](https://github.com/bplugaru/efx-pcrm/issues/142)).
   *
   * Se tipărește **ca atare**: `71`, nu `EFX-ANX-000071`. Nimic de compus la afișare.
   *
   * ⚠️ **`null` nu e „lipsește din răspuns"**, e răspunsul: actul n-a fost emis încă. Ecranele
   * scriu „—" în loc de număr și insigna spune „Ciornă". Vezi `isDraft`.
   */
  documentNumber: z.number().int().nullable(),
  documentDate: z.string(),
  documentState: documentStateSchema,
  /**
   * De ce a ieșit actul din circulație: motivul anulării, sau al rezilierii (#140).
   *
   * ⚠️ **Un singur câmp pentru amândouă** — `documentState` spune care dintre ele s-a
   * întâmplat. La reziliere poate fi `null`, dacă în locul motivului s-a atașat actul de
   * încetare; la anulare, niciodată.
   */
  cancelReason: z.string().nullable(),
  /**
   * Actul de încetare atașat la reziliere, ca **id de document din registru** — sau `null`.
   *
   * Hârtia adusă la reziliere intră în registru cu numărul ei, ca orice act primit; rândul
   * ăsta doar arată spre ea, iar ecranul o deschide pe drumul obișnuit.
   */
  terminationDocumentId: z.uuid().nullable(),
  /**
   * Mențiunile operatorului despre act, sau `null`.
   *
   * **Notă internă**: nu intră în documentul randat și nici în PDF. Se citește pe fișa
   * documentului și se rescrie de acolo, în orice stare — e o notă despre act, nu conținut
   * de act. Vezi [#137](https://github.com/bplugaru/efx-pcrm/issues/137).
   */
  notes: z.string().nullable(),
  /**
   * `1 generat` · `2 primit`.
   *
   * Un act **primit** n-are text în spate: nu se deschide în editor, n-are istoric de
   * versiuni, iar „Vezi PDF" întoarce fișierul care a venit, nu o randare. Vezi
   * `DOCUMENT_SOURCE` din `@pcrm/be/documents`.
   */
  source: z.union([z.literal(1), z.literal(2)]),
  /**
   * Numărul a fost scris de mână, pentru un act adus din sistemul vechi (#239). Doar pe actele
   * astea apare „Corectează numărul".
   */
  manualNumber: z.boolean(),
  /** Semn că fișierul există. Descărcarea trece tot prin `GET /documents/:id/pdf`. */
  filePath: z.string().nullable(),
  fileMime: z.string().nullable(),
  /**
   * Momentul arhivării **documentului**, `null` = document viu (#141).
   *
   * ⚠️ **Nu e `customerArchivedAt`.** Acela e al clientului și se citește de pe fișa lui; ăsta
   * e al actului, se pune și se scoate din meniul documentului. Un act viu al unui client
   * arhivat nu e arhivat, iar dezarhivarea clientului nu scoate actul din arhivă.
   */
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
});

export const DOCUMENT_SOURCE = { generated: 1, received: 2 } as const;

export type DocumentSource = (typeof DOCUMENT_SOURCE)[keyof typeof DOCUMENT_SOURCE];

export type Document = z.infer<typeof documentSchema>;

/**
 * ⚠️ **Exportată ca să poată fi verificată cu un răspuns real al serverului.**
 * Vezi `documents.api.spec.ts` și motivul din 2026-08-21.
 */
export const documentListItemSchema = documentSchema.extend({
  documentTypeCode: z.string(),
  documentTypeName: z.string(),
  companyName: z.string(),
  customerNickname: z.string().nullable(),
  customerName: z.string().nullable(),
  /**
   * Denumirea partenerului, sau `null` când actul e al unui client.
   *
   * ⚠️ **Coloană separată, nu denumirea turnată peste `customerName`**: „partenerul e
   * partener, diferit de client. trebuie să fie clar asta" (fondatorul, 2026-09-17). Un ecran
   * care ar citi o singură denumire n-ar mai putea spune care dintre ele e.
   */
  partnerName: z.string().nullable(),
  /**
   * Momentul arhivării **clientului**, `null` = client nearhivat.
   *
   * ⚠️ **Nu e o stare a documentului.** Actul își păstrează starea lui — `Semnat` rămâne
   * `Semnat`, e dovada — iar ecranul pune „Arhivat" **lângă** ea. O valoare `arhivat` în
   * `documentState` ar fi înlocuit tocmai răspunsul pe care starea îl dă.
   */
  customerArchivedAt: z.string().nullable(),
  /** Abonamentele acoperite. Gol = act care aparține doar clientului. */
  subscriptions: z.array(z.object({ id: z.string(), name: z.string() })),
  createdByName: z.string().nullable(),
});

export type DocumentListItem = z.infer<typeof documentListItemSchema>;

/**
 * Filtrele registrului. Aceleași pentru ecranul „Documente emise" și pentru tabul de pe fișa
 * clientului — al doilea fixează `customerId` și nu arată filtrul de firmă, pentru că un
 * client aparține unei singure firme.
 */
export interface DocumentFilters {
  readonly companyId?: string;
  readonly customerId?: string;
  readonly partnerId?: string;
  readonly documentTypeId?: string;
  readonly documentState?: DocumentState;
  /** Numărul exact, sau codul ori denumirea clientului. */
  readonly search?: string;
  /**
   * Documentele clienților arhivați: lipsă = ascunse · `all` = și ele · `only` = doar ele.
   *
   * ⚠️ **Tabul de pe fișa clientului trimite mereu `all`.** Fără el, deschizi un client
   * arhivat anume ca să-i vezi actele și găsești o listă goală.
   */
  readonly archived?: 'all' | 'only';
  /**
   * Documentele **arhivate ca documente**: lipsă = ascunse · `all` = și ele · `only` = doar ele.
   *
   * ⚠️ **Alt parametru decât `archived`**, care înseamnă „actele clienților arhivați". Tabul
   * de pe fișa clientului trimite `archived: 'all'` mereu; cu un singur parametru ar fi cerut,
   * fără să vrea, și actele arhivate pe care tocmai le ascunde. Vezi #141.
   */
  readonly documentArchived?: 'all' | 'only';
  /**
   * `only` = **doar actele emise**, cele cu număr ([#142](https://github.com/bplugaru/efx-pcrm/issues/142)).
   *
   * ⚠️ **Lipsă = toate, ciornele incluse** — spre deosebire de cele două filtre de arhivare,
   * care restrâng când lipsesc. Ecranul „Documente emise" îl trimite; tabul de pe fișa
   * clientului nu, pentru că acolo se văd chiar ciornele lui.
   */
  readonly issued?: 'only';
  readonly page: number;
  readonly perPage?: number;
}

export const DOCUMENTS_PAGE_SIZE = 25;

export const DOCUMENTS_QUERY_KEY = ['documents'] as const;

function toSearchParams(filters: DocumentFilters): string {
  const params = new URLSearchParams();

  if (filters.companyId) params.set('companyId', filters.companyId);
  if (filters.customerId) params.set('customerId', filters.customerId);
  if (filters.partnerId) params.set('partnerId', filters.partnerId);
  if (filters.documentTypeId) params.set('documentTypeId', filters.documentTypeId);
  if (filters.documentState !== undefined) {
    params.set('documentState', String(filters.documentState));
  }
  if (filters.search) params.set('search', filters.search);
  if (filters.archived) params.set('archived', filters.archived);
  if (filters.documentArchived) params.set('documentArchived', filters.documentArchived);
  if (filters.issued) params.set('issued', filters.issued);
  params.set('page', String(filters.page));
  params.set('perPage', String(filters.perPage ?? DOCUMENTS_PAGE_SIZE));

  return params.toString();
}

const documentListSchema = z.object({
  items: z.array(documentListItemSchema),
  total: z.number(),
  page: z.number(),
  perPage: z.number(),
});

export type DocumentListResult = z.infer<typeof documentListSchema>;

export const documentsQueryOptions = (filters: DocumentFilters) =>
  queryOptions({
    queryKey: [...DOCUMENTS_QUERY_KEY, filters] as const,
    queryFn: () => apiGet(`/documents?${toSearchParams(filters)}`, documentListSchema),
  });

/**
 * Template-urile publicate din care se poate emite tipul ales, pentru clientul ales.
 *
 * ⚠️ **Prin registrul de documente, nu prin `/templating`.** Rutele modulului de
 * template-uri cer `templates.view`, iar sursele de date `templates.sources` — permisiuni de
 * autor și de administrator. Cine emite contracte n-are nevoie de ele: are `documents.manage`
 * și primește exact atât, lista tipului lui și rândurile surselor folosite de câmpuri.
 *
 * Lista goală nu e o eroare: e răspunsul „tipul ăsta n-are încă niciun template publicat".
 */
export const templateChoiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  version: z.number().int(),
});

export type TemplateChoice = z.infer<typeof templateChoiceSchema>;

export const generationTemplatesQueryOptions = (input: {
  customerId: string;
  documentTypeId: string;
}) =>
  queryOptions({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'templates', input] as const,
    queryFn: () =>
      apiGet(
        `/documents/templates?customerId=${input.customerId}&documentTypeId=${input.documentTypeId}`,
        z.object({ items: z.array(templateChoiceSchema) }),
      ).then((body) => body.items),
    // Fără tip ales nu există întrebare; `enabled` se pune la utilizare, unde se știe.
    enabled: input.documentTypeId !== '',
  });

/** Un câmp de completat la generare. `options === null` = text liber. */
export const templateFormFieldSchema = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string(),
  dataType: z.enum(['text', 'number', 'date', 'boolean']),
  required: z.boolean(),
  defaultValue: z.string().nullable(),
  options: z.array(z.object({ value: z.string(), label: z.string() })).nullable(),
});

export type TemplateFormField = z.infer<typeof templateFormFieldSchema>;

export const templateFormQueryOptions = (templateId: string) =>
  queryOptions({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'templates', templateId, 'form'] as const,
    queryFn: () =>
      apiGet(
        `/documents/templates/${templateId}/form`,
        z.object({ fields: z.array(templateFormFieldSchema) }),
      ).then((body) => body.fields),
    enabled: templateId !== '',
  });

/**
 * Răspunsul la un câmp completat: **perechea**, nu doar cheia străină.
 *
 * Un pachet redenumit peste un an ar face ca documentul retipărit să arate altceva decât
 * cel semnat. Pentru text liber, `id` și `text` sunt același lucru.
 */
export interface DocumentFieldValue {
  readonly id: string;
  readonly text: string;
}

/**
 * Generarea: **o cerere, un document**, oricâte abonamente ar acoperi.
 *
 * `subscriptionIds` spune ce **acoperă** actul, nu de câte ori se repetă: un contract cu
 * două servicii e un contract cu două anexe, nu două contracte (fondatorul, 2026-08-21).
 * Gol = document care aparține doar clientului. Ecranul nu cunoaște `CONTRACT_CADRU` și
 * `ANEXA` ca pe cazuri speciale.
 */
export function generateDocument(input: {
  customerId: string;
  documentTypeId: string;
  templateId?: string;
  fieldValues?: Readonly<Record<string, DocumentFieldValue>>;
  subscriptionIds: readonly string[];
}): Promise<DocumentListItem> {
  return apiPost(
    '/documents',
    {
      customerId: input.customerId,
      documentTypeId: input.documentTypeId,
      ...(input.templateId === undefined ? {} : { templateId: input.templateId }),
      fieldValues: input.fieldValues ?? {},
      subscriptionIds: [...input.subscriptionIds],
    },
    z.object({ document: documentListItemSchema }),
  ).then((body) => body.document);
}

/**
 * Încărcarea unui act primit de la client: **o cerere, un document, un număr**.
 *
 * Fișierul și câmpurile pleacă împreună, într-un `multipart/form-data`. Firma **nu e câmp**:
 * un client aparține unei singure firme din grup, iar actul intră în registrul ei — ca la
 * generare și ca la atribuirea din căsuța de email. Vezi `documentUploadSchema` pe server.
 *
 * ⚠️ **Nu e idempotentă**, ca generarea: a doua apăsare consumă alt număr. Butonul se stinge
 * cât ține cererea, dar cine urcă de două ori același contract vede două rânduri în registru
 * și anulează unul, cu motiv — numerele rămân consumate amândouă.
 */
/**
 * Al cui e actul urcat.
 *
 * Uniune discriminată, ca pe server: **un client sau un partener, niciodată amândoi**. La
 * partener vine și firma emitentă, pentru că partenerul e comun grupului și n-are una de
 * moștenit — vezi `documentUploadSchema` și `architecture/tenancy.md`.
 */
export type UploadOwner =
  | { readonly kind: 'customer'; readonly customerId: string }
  | { readonly kind: 'partner'; readonly partnerId: string; readonly companyId: string };

export function uploadDocument(input: {
  file: File;
  owner: UploadOwner;
  documentTypeId: string;
  documentState: 1 | 3;
  /** `YYYY-MM-DD`. Lipsă = azi. */
  documentDate?: string;
  /** Mențiunile operatorului. Lipsă sau gol = niciuna (#137). */
  notes?: string;
  /**
   * Numărul de registru scris de mână, pentru un act din sistemul vechi (#239). Lipsă = următorul
   * număr automat. Trebuie să fie sub pragul firmei și liber — regula e a serverului.
   */
  documentNumber?: number;
}): Promise<DocumentListItem> {
  return apiUpload(
    '/documents/upload',
    input.file,
    z.object({ document: documentListItemSchema }),
    {
      ...(input.owner.kind === 'customer'
        ? { customerId: input.owner.customerId }
        : { partnerId: input.owner.partnerId, companyId: input.owner.companyId }),
      documentTypeId: input.documentTypeId,
      documentState: String(input.documentState),
      ...(input.documentDate === undefined || input.documentDate === ''
        ? {}
        : { documentDate: input.documentDate }),
      // Câmp de `multipart/form-data`: negol sau deloc. Un `''` trimis ar fi ajuns oricum
      // `null` pe server, dar ar fi umplut corpul cererii cu un câmp fără conținut.
      ...(input.notes === undefined || input.notes.trim() === '' ? {} : { notes: input.notes }),
      ...(input.documentNumber === undefined
        ? {}
        : { documentNumber: String(input.documentNumber) }),
    },
  ).then((body) => body.document);
}

/** Se poate edita din `ciornă` și din `trimis`. Copie a regulii de pe server (`canEdit`). */
export function canEdit(state: DocumentState): boolean {
  return state === DOCUMENT_STATE.draft || state === DOCUMENT_STATE.sent;
}
/**
 * Se poate deschide în editor?
 *
 * Starea **și** proveniența: un act primit e un PDF sau o poză, deci editorul n-ar avea ce
 * deschide. Serverul refuză oricum (`DocumentContentMissingError`), dar un buton care duce
 * la un ecran gol e o apăsare pierdută de fiecare dată.
 */
export function canOpenEditor(document: {
  documentState: DocumentState;
  source: DocumentSource;
}): boolean {
  return document.source === DOCUMENT_SOURCE.generated && canEdit(document.documentState);
}

/**
 * Un document din registru, citit după id — cu denumirile rezolvate, ca în listă.
 *
 * Antetul editorului are nevoie de număr, dată, stare și tipul documentului; le primește
 * dintr-o singură cerere, nu din nomenclatorul de tipuri plus fișa clientului.
 */
export const documentQueryOptions = (id: string) =>
  queryOptions({
    queryKey: [...DOCUMENTS_QUERY_KEY, id] as const,
    queryFn: () =>
      apiGet(`/documents/${id}`, z.object({ document: documentListItemSchema })).then(
        (body) => body.document,
      ),
  });

/**
 * Arborele documentului, dinspre client — **aceeași formă ca a unui template**.
 *
 * Nu există o structură „de template" și una „de document": documentul e o copie a
 * arborelui, deci același editor îl deschide. Schemele sunt totuși scrise aici, nu
 * importate din `templates.api`, ca fiecare zonă să-și valideze singură răspunsul —
 * aceeași regulă ca peste tot în `apps/crm`.
 */
const jsonLogicSchema: z.ZodType<DocumentJsonLogic> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonLogicSchema),
    z.record(z.string(), jsonLogicSchema),
  ]),
);

export type DocumentJsonLogic =
  | string
  | number
  | boolean
  | null
  | DocumentJsonLogic[]
  | { [operator: string]: DocumentJsonLogic };

const nodeContentSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), html: z.string() }),
  z.object({
    type: z.literal('table'),
    source: z.string().optional(),
    columns: z.array(
      z.object({
        field: z.string(),
        label: z.string(),
        align: z.enum(['left', 'center', 'right']).optional(),
      }),
    ),
    rows: z.array(z.array(z.string())).optional(),
    onEmpty: z.enum(['hide', 'show']).optional(),
  }),
  /**
   * Un nod predefinit al aplicației — „Anexă", și ce mai vine după. Cheia și proprietățile
   * sunt **opace pentru editor**, exact ca la template: documentul e o copie a arborelui,
   * deci orice tip de conținut care intră într-un template ajunge și aici.
   *
   * ⚠️ **Schema asta e a treia copie**, lângă `libs/tpl/contracts` și `templates.api.ts`.
   * Sunt separate deliberat, ca fiecare zonă să-și valideze singură răspunsul — prețul e că
   * un tip nou de conținut trebuie adăugat în **toate trei**. Când lipsește de aici,
   * serverul trimite corect și ecranul refuză: „Invalid discriminator value. Expected
   * 'text' | 'table'", pe un document care se generase fără reproș.
   */
  z.object({
    type: z.literal('block'),
    key: z.string(),
    props: z.record(z.string(), z.string()).optional(),
  }),
  /** Spațiu între capitole sau pagină nouă. */
  z.object({
    type: z.literal('break'),
    page: z.boolean().optional(),
    heightMm: z.number().optional(),
  }),
]);

export type DocumentNodeContent = z.infer<typeof nodeContentSchema>;

export interface DocumentNodeShape {
  id: string;
  title?: string;
  content?: DocumentNodeContent;
  formula?: DocumentJsonLogic;
  unnumbered?: boolean;
  resetNumbering?: boolean;
  children?: DocumentNodeShape[];
}

const documentNodeSchema: z.ZodType<DocumentNodeShape> = z.lazy(() =>
  z.object({
    id: z.string(),
    title: z.string().optional(),
    content: nodeContentSchema.optional(),
    formula: jsonLogicSchema.optional(),
    unnumbered: z.boolean().optional(),
    resetNumbering: z.boolean().optional(),
    children: z.array(documentNodeSchema).optional(),
  }),
);

const levelNumberingSchema = z.object({
  enabled: z.boolean().optional(),
  listStyle: z
    .enum(['decimal', 'upper-roman', 'lower-roman', 'upper-latin', 'lower-latin'])
    .optional(),
  format: z.string().optional(),
  prefix: z.string().optional(),
});

/**
 * O versiune a documentului: arborele, textul randat și stilul numerelor.
 *
 * `numbering` poate lipsi pe versiunile scrise înainte ca stilul să se copieze pe document —
 * acolo se cade pe implicitul editorului, ca la un template fără numerotare salvată.
 */
export const documentVersionSchema = z.object({
  version: z.number().int(),
  content: z.array(documentNodeSchema),
  renderedHtml: z.string(),
  numbering: z.array(levelNumberingSchema).nullable(),
  headerHtml: z.string().nullable(),
  footerHtml: z.string().nullable(),
  fieldValues: z.record(z.string(), z.object({ id: z.string(), text: z.string() })),
  createdAt: z.string(),
});

export type DocumentVersion = z.infer<typeof documentVersionSchema>;

export const documentContentQueryOptions = (id: string) =>
  queryOptions({
    queryKey: [...DOCUMENTS_QUERY_KEY, id, 'content'] as const,
    queryFn: () =>
      apiGet(`/documents/${id}/content`, z.object({ version: documentVersionSchema })).then(
        (body) => body.version,
      ),
  });

/** Un rând din istoric: cine a salvat și când. Fără text — e o listă, nu N documente. */
export const documentVersionItemSchema = z.object({
  version: z.number().int(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
});

export type DocumentVersionItem = z.infer<typeof documentVersionItemSchema>;

export const documentVersionsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: [...DOCUMENTS_QUERY_KEY, id, 'versions'] as const,
    queryFn: () =>
      apiGet(
        `/documents/${id}/versions`,
        z.object({ items: z.array(documentVersionItemSchema) }),
      ).then((body) => body.items),
  });

/**
 * Salvarea din editor: **o versiune nouă**, nu o suprascriere.
 *
 * Se trimite doar arborele. Numărul, data, câmpurile completate la generare și antetul se
 * poartă mai departe de pe versiunea anterioară — nu sunt câmpuri ale cererii, tocmai ca
 * să nu se poată schimba dintr-un browser.
 */
export function saveDocumentVersion(
  id: string,
  content: readonly DocumentNodeShape[],
): Promise<DocumentVersion> {
  return apiPost(
    `/documents/${id}/versions`,
    { content },
    z.object({ version: documentVersionSchema }),
  ).then((body) => body.version);
}

/** Ce date are aplicația, pentru meniul de inserare al editorului: chei și etichete. */
export const documentDataDescriptorSchema = z.object({
  variables: z.array(z.object({ key: z.string(), label: z.string(), type: z.string() })),
  collections: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      fields: z.array(z.object({ key: z.string(), label: z.string(), type: z.string() })),
    }),
  ),
});

/**
 * ⚠️ **Prin registrul de documente, nu prin `/templating`.** Ruta modulului cere
 * `templates.view`; cine corectează un contract emis are `documents.manage`. Aceeași regulă
 * ca la lista de template-uri de la generare.
 */
export const documentDataDescriptorQueryOptions = () =>
  queryOptions({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'data'] as const,
    queryFn: () => apiGet('/documents/data', documentDataDescriptorSchema),
    // Descriptorul e o constantă a serverului: se schimbă la deploy, nu între două ecrane.
    staleTime: Number.POSITIVE_INFINITY,
  });

/**
 * Nodurile predefinite pe care le poate purta arborele unui document.
 *
 * ⚠️ **Prin registru, nu prin `/templating/blocks`.** Aceeași listă, altă permisiune: ruta
 * modulului cere `templates.view`, iar cine corectează un contract emis are
 * `documents.manage`. Fără ea, o anexă din arborele documentului s-ar desena în editor ca un
 * nod fără etichetă și fără variantă.
 *
 * Opțiunile sunt rânduri de nomenclator, care se schimbă fără deploy — deci nu se
 * cache-uiesc la infinit, spre deosebire de descriptorul de date.
 */
export const documentBlockDescriptorSchema = z.object({
  key: z.string(),
  label: z.string(),
  hint: z.string().optional(),
  group: z.object({ label: z.string(), addLabel: z.string() }).optional(),
  properties: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      options: z.array(z.object({ value: z.string(), label: z.string() })),
    }),
  ),
  labelPrefix: z.string().optional(),
  /**
   * Aceeași formă ca la `/templating/blocks`: e același descriptor, servit pe altă rută
   * pentru altă permisiune. Lipsă de aici, Zod l-ar tăia tăcut, iar cele două scheme ale
   * aceluiași răspuns s-ar despărți.
   */
  previewHtml: z.string().optional(),
});

export type DocumentBlockDescriptor = z.infer<typeof documentBlockDescriptorSchema>;

export const documentBlocksQueryOptions = () =>
  queryOptions({
    queryKey: [...DOCUMENTS_QUERY_KEY, 'blocks'] as const,
    queryFn: () =>
      apiGet('/documents/blocks', z.object({ items: z.array(documentBlockDescriptorSchema) })).then(
        (body) => body.items,
      ),
  });

/**
 * Conținutul documentului, ca blob.
 *
 * Pentru un act **primit** e chiar fișierul venit pe email; pentru unul generat, randarea
 * versiunii. Serverul alege — ecranul cere aceeași rută pentru amândouă.
 *
 * Există separat de `openDocumentPdf` pentru că ecranul documentului îl **arată pe loc**,
 * într-un cadru, în loc să deschidă o filă nouă.
 */
export function fetchDocumentFile(id: string): Promise<Blob> {
  return apiGetBlob(`/documents/${id}/pdf`);
}

/**
 * Deschide PDF-ul documentului emis, într-o filă nouă.
 *
 * Nu un `<a href>` simplu: cererea are nevoie de cookie-ul de sesiune și de tratarea
 * erorilor. Un document emis înainte de generarea din template n-are conținut, iar
 * serverul spune asta cu 409 — un link mort ar fi arătat ca o pagină goală.
 *
 * `version` deschide o versiune anume, din istoric. Lipsă = cea curentă.
 */
export async function openDocumentPdf(id: string, version?: number): Promise<void> {
  const blob = await apiGetBlob(
    version === undefined ? `/documents/${id}/pdf` : `/documents/${id}/pdf?version=${version}`,
  );
  const url = URL.createObjectURL(blob);

  window.open(url, '_blank', 'noopener');
  // Revocarea imediată ar goli fereastra înainte să apuce s-o citească.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Cât poate avea o mențiune. **Copie a regulii de pe server** (`DOCUMENT_NOTES_MAX_LENGTH`).
 *
 * Aici oprește tastarea, ca operatorul să nu scrie o pagină și să afle la salvare că nu
 * încape. Regula care contează e în `documentNotesSchema`.
 */
export const DOCUMENT_NOTES_MAX_LENGTH = 1000;

/**
 * Corectează numărul scris de mână al unui act din sistemul vechi (#239) — „am urcat 2, era 3".
 * Regulile (sub prag, liber, doar numere scrise de mână) sunt ale serverului.
 */
export function correctDocumentNumber(
  id: string,
  documentNumber: number,
): Promise<DocumentListItem> {
  return apiPut(
    `/documents/${id}/number`,
    { documentNumber },
    z.object({ document: documentListItemSchema }),
  ).then((body) => body.document);
}

/**
 * Rescrie mențiunile unui document. Textul gol le șterge.
 *
 * `PUT`, ca pe server: se înlocuiește nota, în întregime. Întoarce documentul cu denumirile
 * rezolvate, ca la citire — ecranul are nevoie de el oricum, iar o a doua cerere ar fi fost
 * o reîncărcare a paginii pentru un singur câmp.
 */
export function updateDocumentNotes(id: string, notes: string): Promise<DocumentListItem> {
  return apiPut(
    `/documents/${id}/notes`,
    // ⚠️ **`null`, nu `''`**: golirea câmpului înseamnă „nu există notă". Serverul traduce și
    // textul gol, dar trimiterea explicită e ce face intenția citibilă din rețea.
    { notes: notes.trim() === '' ? null : notes },
    z.object({ document: documentListItemSchema }),
  ).then((body) => body.document);
}

/**
 * Arhivează sau scoate din arhivă un document anulat (#141).
 *
 * Două rute pe server, o singură funcție aici: ecranul are un singur buton, care își schimbă
 * eticheta după `archivedAt` — exact ca „Arhivează"/„Dezarhivează" de pe fișa clientului. Fără
 * corp: motivul e deja pe act, e `cancelReason`.
 */
export function setDocumentArchived(id: string, archived: boolean): Promise<Document> {
  return apiPost(
    `/documents/${id}/${archived ? 'archive' : 'unarchive'}`,
    {},
    z.object({ document: documentSchema }),
  ).then((body) => body.document);
}

/**
 * **Ștergerea unei ciorne** ([#142](https://github.com/bplugaru/efx-pcrm/issues/142)).
 *
 * `DELETE`, fără corp și fără răspuns: rândul dispare, iar versiunile și sesiunea lui de
 * semnare cad odată cu el. Nu se cere motiv — motivul e al anulării, care e a actelor emise.
 */
export function deleteDocument(id: string): Promise<void> {
  return apiDelete(`/documents/${id}`);
}

export function cancelDocument(id: string, reason: string): Promise<Document> {
  return apiPost(
    `/documents/${id}/cancel`,
    { reason },
    z.object({ document: documentSchema }),
  ).then((body) => body.document);
}

/**
 * Rezilierea unui act semnat: **motiv, act de încetare, sau amândouă** (#140).
 *
 * ⚠️ **Două drumuri către aceeași rută, după cum a completat operatorul.** Cu fișier pleacă
 * `multipart/form-data`, ca la încărcare — actul de încetare intră în registru cu numărul lui.
 * Fără fișier pleacă JSON, pentru că un `multipart` cu un singur câmp de text ar fi cerut un
 * `File` inventat. Serverul citește aceleași câmpuri din amândouă.
 *
 * ⚠️ **Nu e idempotentă când vine cu fișier**: a doua apăsare ar consuma alt număr de registru
 * pentru încă un act de încetare. Butonul se stinge cât ține cererea, iar serverul refuză a
 * doua reziliere a aceluiași act (`document_not_terminable`).
 */
export function terminateDocument(input: {
  id: string;
  /** Lipsă sau gol = fără motiv scris; atunci fișierul e obligatoriu. Serverul verifică. */
  reason?: string;
  file?: File;
  /** Tipul actului de încetare urcat. Obligatoriu cu fișier, altfel nu se trimite. */
  documentTypeId?: string;
  /** Data actului de încetare, `YYYY-MM-DD`. Lipsă = azi. */
  documentDate?: string;
}): Promise<Document> {
  const reason = input.reason?.trim() ?? '';
  const response = z.object({ document: documentSchema });

  if (input.file === undefined) {
    return apiPost(`/documents/${input.id}/terminate`, { reason }, response).then(
      (body) => body.document,
    );
  }

  return apiUpload(`/documents/${input.id}/terminate`, input.file, response, {
    // Câmp de `multipart/form-data`: negol sau deloc, ca la mențiuni. Un `''` ar fi ajuns
    // oricum `null` pe server, dar ar fi umplut corpul cu un câmp fără conținut.
    ...(reason === '' ? {} : { reason }),
    ...(input.documentTypeId === undefined ? {} : { documentTypeId: input.documentTypeId }),
    ...(input.documentDate === undefined || input.documentDate === ''
      ? {}
      : { documentDate: input.documentDate }),
  }).then((body) => body.document);
}
